import { useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Play, ArrowLeft, X, Clock, Calendar, Eye } from "lucide-react";
import { fmtCount, useViewCount } from "../../lib/text";
import { VideoPlayer, videoSrc } from "../../components/site/VideoPlayer";
import { fmtDuration, isoDuration } from "../../lib/video";
import { useSite, usePublic } from "../../lib/site";
import { href, fmtDate } from "../../lib/i18n";
import { AlbumCard, ShareButtons } from "../../components/site/Cards";
import { Img } from "../../components/site/Common";
import { Seo, breadcrumbLd } from "../../components/site/Seo";
import { PageHeader } from "./PageView";
import NotFound, { Loading } from "./NotFound";

export function Videos() {
  const { t, ui } = useSite();
  const [params, setParams] = useSearchParams();
  const cat = params.get("kategori") || "";
  const { data: cats = [] } = usePublic(["video-cats"], "/public/video-categories");
  const { data: albums, isLoading } = usePublic(["albums", cat], `/public/albums${cat ? `?category=${cat}` : ""}`);
  const pill = (a) => `px-4 py-2 rounded-full text-sm font-medium border transition-colors ${a ? "bg-s-primary text-s-onprimary border-transparent" : "border-site-border hover:border-site-primary"}`;
  return (
    <section data-testid="video-gallery-section">
      <Seo title={ui.videos} />
      <PageHeader eyebrow={ui.videos} title={ui.featuredVideos} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap gap-2 mb-12" data-testid="video-category-tabs">
          <button className={pill(!cat)} onClick={() => setParams({})} data-testid="video-cat-all">{ui.all}</button>
          {cats.map((c) => <button key={c.id} className={pill(cat === c.slug)} onClick={() => setParams({ kategori: c.slug })} data-testid={`video-cat-${c.slug}`}>{t(c.name)}</button>)}
        </div>
        {isLoading ? <Loading /> : albums?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{albums.map((a) => <AlbumCard key={a.id} a={a} />)}</div>
        ) : cat ? <p className="text-s-muted text-center py-20">{ui.noResults}</p> : null}
        {!cat && <StandaloneVideos />}
      </div>
    </section>
  );
}

function VideoCard({ v }) {
  const { lang, t } = useSite();
  return (
    <Link to={href(lang, "video", v.slug)} data-testid={`video-card-${v.slug}`} className="group text-left">
      <div className="relative aspect-video rounded-[var(--s-radius-sm)] overflow-hidden bg-black">
        {v.duration > 0 && <span className="absolute bottom-2 right-2 z-10 rounded bg-black/80 px-1.5 py-0.5 text-[11px] text-white">{fmtDuration(v.duration)}</span>}
        <Img src={v.thumbnail} alt={t(v.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-black/25 group-hover:bg-black/40 transition-colors flex items-center justify-center">
          <span className="w-16 h-16 rounded-full bg-s-primary text-s-onprimary flex items-center justify-center group-hover:scale-110 transition-transform"><Play fill="currentColor" size={22} /></span>
        </div>
      </div>
      <p className="mt-4 font-semibold group-hover:text-s-primary transition-colors">{t(v.title)}</p>
    </Link>
  );
}

function StandaloneVideos() {
  const { lang } = useSite();
  const { data = [] } = usePublic(["videos-standalone"], "/public/videos?standalone=true");
  if (!data.length) return null;
  return (
    <div className="mt-20" data-testid="standalone-videos">
      <p className="eyebrow mb-8">{lang === "tr" ? "Videolar" : "Videos"}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.map((v) => <VideoCard key={v.id} v={v} />)}</div>
    </div>
  );
}

export function AlbumView() {
  const { slug } = useParams();
  const { lang, t, ui } = useSite();
  const { data: a, isLoading, isError } = usePublic(["album", slug], `/public/albums/${slug}`);
  if (isLoading) return <Loading />;
  if (isError || !a) return <NotFound />;
  const vids = a.videos.filter((v) => v.status !== "draft");
  const ld = { "@context": "https://schema.org", "@type": "ItemList", itemListElement: vids.map((v, i) => ({ "@type": "ListItem", position: i + 1, url: window.location.origin + href(lang, "video", v.slug) })) };
  return (
    <section data-testid="album-view">
      <Seo title={t(a.seo?.title) || t(a.title)} description={t(a.seo?.description) || t(a.description)} image={a.cover} jsonLd={[ld, breadcrumbLd([{ name: ui.videos, path: href(lang, "videos") }, { name: t(a.title), path: href(lang, "album", a.slug) }])]} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-12">
        <Link to={href(lang, "videos")} className="inline-flex items-center gap-2 text-sm text-s-muted hover:text-s-primary" data-testid="album-back-link"><ArrowLeft size={15} /> {ui.videos}</Link>
      </div>
      <PageHeader eyebrow={a.category ? t(a.category.name) : ui.videos} title={t(a.title)} sub={t(a.description)} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {vids.map((v) => <VideoCard key={v.id} v={v} />)}
      </div>
    </section>
  );
}

export function VideoView() {
  const { slug } = useParams();
  const { lang, t, ui, site } = useSite();
  const { data: v, isLoading, isError } = usePublic(["video", slug], `/public/videos/${slug}`);
  const local = useViewCount("video", v?.slug, v?.views);
  if (isLoading) return <Loading />;
  if (isError || !v) return <NotFound />;
  const title = t(v.title);
  const desc = t(v.description);
  const abs = (u) => (u?.startsWith("/") ? window.location.origin + u : u);
  const path = href(lang, "video", slug);
  const crumbs = [{ name: ui.videos, path: href(lang, "videos") }, ...(v.album ? [{ name: t(v.album.title), path: href(lang, "album", v.album.slug) }] : []), { name: title, path }];
  const ld = {
    "@context": "https://schema.org", "@type": "VideoObject", name: title,
    ...((v.ext_views ?? local) ? { interactionStatistic: { "@type": "InteractionCounter", interactionType: { "@type": "WatchAction" }, userInteractionCount: v.ext_views ?? local } } : {}), description: (desc || title).slice(0, 2000),
    thumbnailUrl: [abs(v.thumbnail)].filter(Boolean), uploadDate: v.published_at || v.created_at, duration: isoDuration(v.duration),
    ...(v.source === "upload" ? { contentUrl: videoSrc(v) } : { embedUrl: v.embed_url }),
    publisher: { "@type": "Person", name: t(site.general?.site_name) || site.profile?.name },
    url: window.location.origin + path,
  };
  return (
    <article data-testid="video-view">
      <Seo title={t(v.seo?.title) || title} description={t(v.seo?.description) || desc} image={v.thumbnail} type="video.other"
        jsonLd={[ld, breadcrumbLd(crumbs)]} />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12">
        <Link to={v.album ? href(lang, "album", v.album.slug) : href(lang, "videos")} className="inline-flex items-center gap-2 text-sm text-s-muted hover:text-s-primary" data-testid="video-back-link"><ArrowLeft size={15} /> {v.album ? t(v.album.title) : ui.videos}</Link>
        <div className="mt-6 aspect-video rounded-[var(--s-radius-sm)] overflow-hidden bg-black shadow-2xl"><VideoPlayer v={v} title={title} /></div>
        <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-12">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="video-title">{title}</h1>
            <div className="mt-3 flex flex-wrap gap-5 text-sm text-s-muted">
              {(v.published_at || v.created_at) && <span className="flex items-center gap-1.5"><Calendar size={14} />{fmtDate(v.published_at || v.created_at, lang)}</span>}
              {v.duration > 0 && <span className="flex items-center gap-1.5"><Clock size={14} />{fmtDuration(v.duration)}</span>}
              {(v.ext_views ?? local) > 0 && <span className="flex items-center gap-1.5" data-testid="video-views"><Eye size={14} />{fmtCount(v.ext_views ?? local, lang)} {lang === "tr" ? "izlenme" : "views"}</span>}
            </div>
            {desc && <p className="mt-8 leading-relaxed whitespace-pre-line text-s-text/90" data-testid="video-description">{desc}</p>}
            <div className="mt-10 pt-8 border-t border-site-border"><ShareButtons title={title} testid="video-share-button" kind="album" shareUrl={v.album?.slug ? `${window.location.origin}/api/public/share/album/${v.album.slug}` : undefined} /></div>
          </div>
          {v.related?.length > 0 && (
            <aside className="space-y-4" data-testid="video-related">
              <p className="eyebrow">{lang === "tr" ? "Diğer videolar" : "More videos"}</p>
              {v.related.map((r) => (
                <Link key={r.id} to={href(lang, "video", r.slug)} className="flex gap-3 group" data-testid={`related-video-${r.slug}`}>
                  <Img src={r.thumbnail} alt={t(r.title)} className="w-32 aspect-video object-cover rounded-md shrink-0" />
                  <span className="text-sm font-medium line-clamp-3 group-hover:text-s-primary transition-colors">{t(r.title)}</span>
                </Link>
              ))}
            </aside>
          )}
        </div>
      </div>
    </article>
  );
}

export function Gallery() {
  const { lang, t, ui } = useSite();
  const { data, isLoading, isError } = usePublic(["gallery"], "/public/gallery");
  if (isLoading) return <Loading />;
  if (isError) return <NotFound />;
  return (
    <section data-testid="photo-gallery-section">
      <Seo title={ui.gallery} />
      <PageHeader eyebrow={ui.gallery} title={ui.gallery} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.map((a) => (
          <Link key={a.id} to={href(lang, "gallery", a.slug)} className="group block" data-testid={`gallery-album-${a.slug}`}>
            <div className="aspect-[4/3] overflow-hidden rounded-[var(--s-radius-sm)]"><Img src={a.cover} alt={t(a.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
            <h3 className="font-heading text-xl mt-4">{t(a.title)}</h3>
            <p className="text-sm text-s-muted">{a.photo_count} {ui.photos}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function GalleryAlbum() {
  const { slug } = useParams();
  const { t } = useSite();
  const [open, setOpen] = useState(null);
  const { data: a, isLoading, isError } = usePublic(["gallery", slug], `/public/gallery/${slug}`);
  if (isLoading) return <Loading />;
  if (isError || !a) return <NotFound />;
  return (
    <section data-testid="gallery-album-view">
      <Seo title={t(a.title)} description={t(a.description)} image={a.cover} />
      <PageHeader title={t(a.title)} sub={t(a.description)} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 columns-1 sm:columns-2 lg:columns-3 gap-5">
        {a.photos.map((p) => (
          <button key={p.id} onClick={() => setOpen(p)} className="mb-5 block w-full" data-testid={`gallery-photo-${p.id}`}>
            <Img src={p.image} alt={t(p.caption) || t(a.title)} className="w-full rounded-xl hover:opacity-90 transition-opacity" />
          </button>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" onClick={() => setOpen(null)} data-testid="gallery-lightbox">
          <button className="absolute top-5 right-5 text-white" aria-label="close"><X size={28} /></button>
          <figure onClick={(e) => e.stopPropagation()} className="max-w-5xl">
            <Img src={open.image} alt={t(open.caption)} className="max-h-[80vh] w-auto rounded-lg" />
            {t(open.caption) && <figcaption className="text-white mt-3 text-center">{t(open.caption)}</figcaption>}
          </figure>
        </div>
      )}
    </section>
  );
}
